import React, {
  useState,
  useEffect,
  useLayoutEffect,
  Suspense,
  lazy,
  FormEvent,
  useRef,
} from "react";
import Log from "../utils/Log";
import {
  redirectToPlans,
  redirectToThankYouPage,
  redirectToThankYouRenewalPage,
  redirectToUpsellPage
} from "../utils/routes";
import { faKey } from "@fortawesome/free-solid-svg-icons";
import useModal from "./helpers/useModal";
import AdblockDetector from "../components/AdBlock";
import Loader from "./Loader";
import { useDispatch, useSelector } from "react-redux";
import currentUserInfo from "../selectors/currentUserInfo";
import currentSaleData from "../selectors/currentSaleData";
import currentSaleContext from "../selectors/currentSaleContext";
import {
  PaymentPlatforms,
  PlanType,
  ProductType,
} from "../reducers/models";
import { fetchSale, setTotalDiscountedCost } from "../actions/sale";
import { updateSeatQuantity } from "../actions/cart";
import selectGAClientId from "../selectors/selectGAClientId";
import { getItems, getDiscountedTotal } from "../selectors/selectCart";
import { getPlanDiscountInfo } from "../utils/saleUtils";
import GenericLoader from "./helpers/GenericLoader";
import { MorePaymentOptionsButton } from "./MorePaymentOptionsButton";
import { useLocation } from "react-router-dom";
import { useElements, useStripe } from "@stripe/react-stripe-js";
import { TokenResult } from "@stripe/stripe-js";
import { purchasePlan, renewPlan } from "../actions/account";
import { faEnvelope } from "@fortawesome/free-solid-svg-icons";
import { validateEmail } from "../utils/validationForm";
import { setAccountlessPurchaseEmail } from "../actions/auth";

import PromotionalBanner from "../components/PromotionalBanner";
import checkStylesForm from "../styles/CheckoutForm.module.scss";
import styles from "../styles/BillingFormStripe.module.scss";
//import { downloadBuildboxClient } from "../utils/downloadsUtils";
//import { toast } from "react-toastify";

const GenericInput = lazy(() => import("./helpers/genericInput"));
const OrderSummary = lazy(() => import("./helpers/orderSummary"));
const CheckoutForm = lazy(() => import("../components/CheckoutForm"));
const GenericModal = lazy(() => import("./helpers/GenericModal"));
const PriceConfirmation = lazy(() => import("../components/PriceConfirmation"));

export const PRODUCT_NAME_MAP = {
  [ProductType.BB4]: "Buildbox 4",
  [ProductType.BB3]: "Buildbox 3",
  [ProductType.BB2]: "Buildbox Classic",
  [ProductType.BBBundle]: "The Buildbox",
  [ProductType.Soundbox]: "Soundbox",
};

export type Props = {
  history: string[];
};

export type PlanInfo = bb.model.ISubscription & {
  cartId: string;
};

export default function BillingFormStripe(props: Props) {
  const dispatch = useDispatch();
  const couponData = useSelector(currentSaleData);
  const saleContext = useSelector(currentSaleContext);
  const currentUser = useSelector(currentUserInfo);
  const googleClientId = useSelector(selectGAClientId);
  const location = useLocation()
  const queryParameters = new URLSearchParams(location.search)
  const totalDiscountedCost = useSelector(getDiscountedTotal);

  //TODO: support multiple product cart purchases
  const selectedCartItems = useSelector(getItems)[0];

  const { isShowing, toggle } = useModal();

  const user = currentUser === null ? { uuid: "", email: "", isNewUser: true } : currentUser;

  const { uuid, email, isNewUser } = user;
  //const { item, cartId } = selectedCartItems;
  const item = selectedCartItems ? selectedCartItems.item : undefined;
  const cartId = selectedCartItems ? selectedCartItems.cartId : undefined;
  const itemDiscountInfo = item ? getPlanDiscountInfo(item, couponData) : null;
  const activeSale = (item && itemDiscountInfo !== null && item.discountsAllowed);

  const [planInfo, setPlanInfo] = useState<PlanInfo | null>(null);

  const [quantity, updateQuantity] = useState(1);

  const [purchaseCost, updatePurchaseCost] = useState<number | null>(null);
  const [saleCode, setSaleCode] = useState<string | null>(null);
  const [renew, setRenew] = useState<string>("")
  const [xsollaAllowed, setXsollaAllowed] = useState<boolean>(true)
  const stripe = useStripe();
  const elements = useElements();
  const processingRef = useRef(false)
  const [openEmailPrompt, setEmailPrompt] = useState<boolean>(false)
  const [errorText, updateErrorMessage] = useState<string>("");
  const [ inputEmail, updateEmail] = useState<string>("");

  async function onLoad() {

    try {
      if(!item || !cartId) {
        redirectToPlans(props, "", "", "");
        return
      }

      dispatch(setAccountlessPurchaseEmail(undefined))

      if (item.planType === PlanType.Free) {
        const promoCode = sessionStorage.getItem("promoCode");
        redirectToPlans(props, "", "", promoCode);
      }

      if (selectedCartItems.seatQuantity) {
        updateQuantity(selectedCartItems.seatQuantity);
      }

      // xsolla has a bug "error 3035" that prevents us from using it for subs with trials since change_plan fails
      if(item.trialDays > 0 && process.env.REACT_APP_DISABLE_XSOLLA_FOR_PLANS_WITH_TRIAL_PERIODS === "true") {
        setXsollaAllowed(false)
      }

      setPlanInfo({ cartId, ...item });
      sessionStorage.setItem("lastPaymentProvider", "stripe");

      //downloadOnLoad(); // disabled due to user feedback
    } catch (error:any) {
      let message = error.message
      Log.error("Checkout Component: error caught on onLoad " + message);
      const promoCode = sessionStorage.getItem("promoCode");
      redirectToPlans(props, "", "", promoCode);
    }
  }

  /*
  async function downloadOnLoad() {
    const noDownload = queryParameters.get("nodl");
    if(noDownload) {
      return;
    }

    async function attemptToDownload() {
      try {
        if (item.productType) {
          const response = await downloadBuildboxClient(null, item.productType);
          Log.info(
            "response from auto download on load after getting gcid",
            response
          );
          return response;
        } 
        else {
          console.log("not sure what to download; skipping")
          return null;
        }
      } 
      catch (e) {
        Log.error("attempt to download failed:", e);
      }
    }

    let result = await attemptToDownload();
    if(result) {
      toast.success("Downloading now!", {
        autoClose: 5000,
      });
    }
  }
    */

  useEffect(() => {
    const tempRenew = queryParameters.get("renew");
    if(tempRenew){
      setRenew(tempRenew)
    }
  }, [queryParameters])

  useLayoutEffect(() => {
    dispatch(fetchSale()); //eslint-disable-next-line
  }, []);

  useEffect(() => {
    onLoad(); //eslint-disable-next-line
  }, [currentUser]);

  useEffect(() => {
    if (activeSale) {
      //calculate the discounted cost if there is couponData
      //should recalculate when quantity is updated or if the sale has changed/ended

      const { saleCode } = couponData;
      if (typeof totalDiscountedCost !== "number") {
        throw new Error("");
      }

      Log.trace(`got coupon data: ${couponData} with sale code: ${saleCode}`);
      setSaleCode(saleCode);
      dispatch(setTotalDiscountedCost(totalDiscountedCost));
      updatePurchaseCost(totalDiscountedCost);
    } else {
      Log.trace("no coupon data right now");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [couponData, totalDiscountedCost, quantity]);

  useEffect(() => {
    if (saleContext.saleJustEnded) {
      handleModalToggle();
    } //eslint-disable-next-line
  }, [saleContext.saleJustEnded]);

  //hook this up to the trigger when price changes to toggle modal
  function handleModalToggle() {
    toggle();
    window.history.replaceState(null, "plans", window.location.pathname);
  }

  async function handleSubmitClick(
    e: FormEvent,
    paymentMethod: { id: string },
    result: TokenResult,
    name: string
  ) {
    e.preventDefault()

    // Pass token & seat quantity to backend and wait for purchase confirmation
    const { id } = paymentMethod;

    const paymentLog = {
      // "got token: ": result.token,
      // "got payment method: ": result.token,
      // "got payment id: ": id,
      // "got accessToken: ": accessToken,
      "got uuid: ": uuid,
      "got email: ": email,
      "got clientId: ": googleClientId,
      // "plan info: ": planInfo,
      // "number of seats: ": quantity,
      // "arg mail": mail,
      // "arg name": name,
      // "arg stripe": stripe
    };
    Log.trace(
      paymentLog,
      "Checkout component: handlePayment (handleSubmitClick)"
    );

    //purchaseCost and saleCode are optional and used when calculating cost manually for sales
    try {
      if(processingRef.current){
        return
      }
      processingRef.current = true

      if ( renew.length > 1 ) {
        await dispatch(renewPlan(id, quantity, purchaseCost, saleCode, name));
      }  
      else {
        await dispatch(purchasePlan(id, quantity, purchaseCost, saleCode, name));
      }

      if (!stripe || !item || !elements) {
        Log.warn("Stripe load error!", "Checkout");

        // throw an error so the user can see 
        throw new Error("Payment processor is unavailable, your card has not been charged. Please contact support, try again, or try another payment platform.")
      }

     if( renew.length > 1) {
        // setTimeout(() => {
        //   redirectToThankYouRenewalPage(props);
        // }, 3000);
        redirectToThankYouRenewalPage(props);
      }
      else if(item.hasUpsell) {
        // this is the old stripe elements checkout container; it doesn't have a Checkout Session
        redirectToUpsellPage(props, item.productType, PaymentPlatforms.Stripe, item.planType, undefined, undefined);
      }
      else {
        // setTimeout(() => {
        //   successRedirect();
        // }, 3000);
        successRedirect();
      }
    } 
    catch (error:any) {
      Log.error(error, "Checkout Component: error caught /updateSubscription");
      throw new Error(error);
    }
  }

  function successRedirect() {
    redirectToThankYouPage(props, "purchase");
  }

  // Assume empty input resets to 1 license
  function handleEmptyLicenseBlur() {
    updateQuantity(1);

    dispatch(updateSeatQuantity(planInfo?.cartId as string, 1));
  }

  function handleLicenseInputFieldChange(
    inputName: string | undefined,
    value: string
  ) {
    if (inputName === "licenseCount") {
      let numValue = parseInt(value, 10);
      if (numValue < 1) {
        numValue = 1;
      }

      updateQuantity(numValue);

      if (!isNaN(numValue)) {
        dispatch(updateSeatQuantity(planInfo?.cartId as string, numValue));
      } else {
        Log.error(`Bad license input change! "${value}"`);
      }
    }
  }

  const modalStyle = {
    maxWidth: "500px",
    padding: "3rem",
    backgroundColor: "#fff",
  };

  const formStyles = {
    title: "Checkout",
    buttonCTA: "Subscribe",
    alignItems: "center",
    backgroundColor: "#fff",
    border: true,
    darkMode: false,
    paymentFormBackground: "white",
    height: "100%",
    width: "100%",
    padding: "20px",
  };

  const inputStyles = {
    container: checkStylesForm.paymentInputWrapper,
    label: checkStylesForm.paymentInputLabel,
    input: checkStylesForm.paymentInput,
    errorMessage: checkStylesForm.paymentErrorText,
    icon: checkStylesForm.inputIcon,
  };


  // if loading sale info
  if (saleContext.showSalesLoader) {
    return (
      <div className={styles.discountInfoLoadingContainer}>
        <GenericLoader />
      </div>
    );
  }

  const handleToXsollaCheckout = () => {

    if (currentUser?.email){
      window.location.href = `/purchaseThroughXsolla?nodl=1`
    } else {
      setEmailPrompt(true)
    }
  }

  const handleEmailPromptModalClose = () => {
    setEmailPrompt(false)
  }

  function handleEmailChange(inputName:string, value:string) {
    updateEmail(value);
  }


  const proceedToXsollaCheckout = () => {
    if(validateEmail(inputEmail)){
      dispatch(setAccountlessPurchaseEmail(inputEmail))
      window.location.href = `/purchaseThroughXsolla?nodl=1`
    } else {
      updateErrorMessage("Please enter a valid email.")
    }
  }

if (item && planInfo) {
    const productName = PRODUCT_NAME_MAP[planInfo.productType]

    return (
      <AdblockDetector>
        <Suspense fallback={<Loader />}>
          <GenericModal
            isShowing={isShowing}
            hide={toggle}
            hideClose
            modalStyle={modalStyle}
            darkMode={false}
            modalHeaderStyle={undefined}
          >
            <PriceConfirmation price={undefined} />
          </GenericModal>

          <GenericModal
            isShowing={openEmailPrompt}
            hide={handleEmailPromptModalClose}
            hideClose={true}
            modalStyle={modalStyle}
            darkMode={false}
            modalHeaderStyle={undefined}
          > <div className={checkStylesForm.payment}>
              <div className={styles.emailPrompt}>
                <h2>Please enter an email so we can setup your Buildbox account.</h2>
                <div style={{paddingBottom: "8px"}}/>
                <h2>Welcome to the family!</h2>
                <div style={{paddingBottom: "8px"}}/>
                <p>If you currently have a Buildbox account, you should enter that email here. Thank you!</p>
                <div style={{paddingBottom: "8px"}}/>
                <GenericInput
                  type={"email"}
                  title={"Email:"}
                  placeholder={""}
                  inputName={"email"}
                  inputValue={inputEmail}
                  changeCallback={handleEmailChange}
                  errorMessage={errorText}
                  styles={inputStyles}
                  icon={faEnvelope}
                />
              </div>
              <div className={styles.buttonsSubmit}>
                <button
                  onClick={() => { setEmailPrompt(false) }}
                  className={ styles.darkCheckoutButton }
                >
                  Close
                </button>
                <button
                  disabled={validateEmail(inputEmail) ? false : true}
                  className={ styles.checkoutButton }
                  onClick={() => proceedToXsollaCheckout()}
                >
                  Go To Checkout
                </button>
              </div>
          </div>
          </GenericModal>

          <div
            className={styles.saleBackground}
          >
            <PromotionalBanner placement={"website_stripe"} router={props} />
            <div
              className={styles.payment}
            >
              <div className={styles.header}>
                <h1>
                  { renew.length > 1 ?
                  <>
                    Renew {renew}
                  </>
                  :
                    <>
                     Get {productName}{" "}
                    {planInfo.planName}
                    </>
                  }

                </h1>
                <h2>Get started</h2>
              </div>
              <div className={styles.additionalSteps}>
                <h3 className={styles.title}>License Seats</h3>
                <div className={styles.licenseText}>
                  Choose the number of license seats you need for your payment
                  plan. Each license seat is for one user and lets each user
                  install Buildbox on 2 computers:
                </div>
                <GenericInput
                  type={"number"}
                  inputName={"licenseCount"}
                  inputValue={quantity + ""}
                  changeCallback={handleLicenseInputFieldChange}
                  focusOutCallback={handleEmptyLicenseBlur}
                  icon={faKey}
                  styles={{
                    container: styles.licenseWrapper,
                    icon: styles.licenseIcon,
                    input: styles.licenseInput,
                  }}
                />
              </div>
              <div className={styles.wrapper}>
                <div>

                  <CheckoutForm
                    formStyles={formStyles}
                    handleSubmitCallback={handleSubmitClick}
                    email={currentUser?.email}
                    loadingMessage={"Payment processing..."}
                    successHeaderMessage={"Payment Successful!"}
                    successMessage={
                      isNewUser
                        ? "Redirecting you to the downloads page..."
                        : "Please sign out of the Buildbox client and sign back in to see the changes applied to your account."
                    }
                    planType={item.planType}
                  >
                    { (renew.length < 1 && xsollaAllowed)  && <MorePaymentOptionsButton handleToXsollaCheckout={handleToXsollaCheckout} />}
                  </CheckoutForm>
                </div>
                <OrderSummary
                  purchaseData={{
                    item: planInfo,
                    quantity: quantity,
                  }}
                  routerProps={props}
                  styles={styles}
                />
              </div>
            </div>
          </div>
        </Suspense>
      </AdblockDetector>
    );
  } else {
    return (
      <AdblockDetector>
        <div>Error, no plan info!</div>
      </AdblockDetector>
    );
  }
}
